import { useCallback, useEffect, useRef } from "react";

interface SwipeDirection {
  direction: "up" | "down" | "left" | "right" | "";
  distance: number;
}

interface UseSwipeDetectionProps {
  onSwipe: (direction: string) => void;
  threshold?: number; // Minimum distance for a swipe
  preventDefault?: boolean;
  isActive?: boolean; // Only detect swipes when active
  targetElement?: HTMLElement | null; // Target element for swipe detection
}

export const useSwipeDetection = ({
  onSwipe,
  threshold = 50,
  preventDefault = true,
  isActive = true,
  targetElement = null,
}: UseSwipeDetectionProps) => {
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );
  const touchEndRef = useRef<{ x: number; y: number; time: number } | null>(
    null
  );

  const getSwipeDirection = useCallback((): SwipeDirection => {
    if (!touchStartRef.current || !touchEndRef.current) {
      return { direction: "", distance: 0 };
    }

    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const deltaTime = touchEndRef.current.time - touchStartRef.current.time;

    // Check if swipe was fast enough (less than 500ms)
    if (deltaTime > 500) {
      return { direction: "", distance: 0 };
    }

    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Determine if it's a horizontal or vertical swipe
    if (absDeltaX > absDeltaY) {
      // Horizontal swipe
      if (absDeltaX < threshold) {
        return { direction: "", distance: absDeltaX };
      }
      return {
        direction: deltaX > 0 ? "right" : "left",
        distance: absDeltaX,
      };
    } else {
      // Vertical swipe
      if (absDeltaY < threshold) {
        return { direction: "", distance: absDeltaY };
      }
      return {
        direction: deltaY > 0 ? "down" : "up",
        distance: absDeltaY,
      };
    }
  }, [threshold]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      // Only handle swipes when active
      if (!isActive) return;

      // Check if touch is within target element (if specified)
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const touch = e.touches[0];
        if (
          touch.clientX < rect.left ||
          touch.clientX > rect.right ||
          touch.clientY < rect.top ||
          touch.clientY > rect.bottom
        ) {
          return; // Touch is outside target element
        }
      }

      if (preventDefault) {
        e.preventDefault();
      }

      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      touchEndRef.current = null;
    },
    [preventDefault, isActive, targetElement]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // Only handle swipes when active and we have a start position
      if (!isActive || !touchStartRef.current) return;

      if (preventDefault) {
        e.preventDefault();
      }

      const touch = e.touches[0];
      touchEndRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
    },
    [preventDefault, isActive]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      // Only handle swipes when active and we have a start position
      if (!isActive || !touchStartRef.current) return;

      if (preventDefault) {
        e.preventDefault();
      }

      // If no touch move occurred, use current position
      if (!touchEndRef.current) {
        const touch = e.changedTouches[0];
        touchEndRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now(),
        };
      }

      const swipe = getSwipeDirection();
      if (swipe.direction) {
        onSwipe(swipe.direction);
      }

      // Reset
      touchStartRef.current = null;
      touchEndRef.current = null;
    },
    [preventDefault, isActive, getSwipeDirection, onSwipe]
  );

  useEffect(() => {
    // Only add event listeners when active
    if (!isActive) return;

    const element = targetElement || document.body;

    element.addEventListener("touchstart", handleTouchStart, {
      passive: !preventDefault,
    });
    element.addEventListener("touchmove", handleTouchMove, {
      passive: !preventDefault,
    });
    element.addEventListener("touchend", handleTouchEnd, {
      passive: !preventDefault,
    });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    preventDefault,
    isActive,
    targetElement,
  ]);

  return {
    // Expose methods if needed for manual control
    getSwipeDirection,
  };
};
